/* The PTL lane of a B2B order for the Zone row (PTL_MERCHANT_SETUP.md). The order keeps the combined zone code it was
   priced on (merchantB2BZoneCode = pickup zone id + drop zone id, merchant zone map applied); the PTL TAT grid names
   those zones. An order without a current code (created outside order creation, or on the old zones) gets the lane
   of today's zone map for its pincodes, the way pricing reads it (the merchant's own pincode, else the standard
   map), marked fromMap because the order did not store it. Null when neither gives a lane, so the row keeps showing
   the order's own zone. */
import { useEffect, useState } from 'react'
import { fetchTatGrid, fetchZoneList } from '../../ptl-merchant-setup/ptlSetupApi'

const NOT_SERVICEABLE = '9999'

// One grid read per page load, shared by every order opened: zone names and lane codes do not change between orders.
let grid = null

const loadGrid = () => {
  if (!grid) {
    grid = fetchTatGrid()
      .then((g) => {
        const zones = new Map((g?.zones || []).map((z) => [z.id, z]))
        const lanes = new Map((g?.cells || []).map((c) => [c.zone, { from: zones.get(c.pickupZoneId), to: zones.get(c.dropZoneId) }]))
        return { zones, lanes }
      })
      .catch((e) => {
        grid = null // no PTL access or a failed read: try again on the next order, show the order's zone meanwhile
        throw e
      })
  }
  return grid
}

// Zone id per pincode: the merchant's own row where it has one, else the standard map (a non-PTL merchant has no own map).
const zoneIdsOf = async (pincodes, merchantId) => {
  const ask = (params) => fetchZoneList({ pincodes: pincodes.join(','), size: pincodes.length, ...params }).then((p) => p?.rows || [])
  const [own, std] = await Promise.all([merchantId ? ask({ merchantId }).catch(() => []) : [], ask({})])
  const out = new Map(std.map((r) => [String(r.pincode), r.zoneId]))
  own.forEach((r) => out.set(String(r.pincode), r.zoneId))
  return out
}

export default function useB2BLane(order) {
  const isB2B = order?.shipment_type === 'B2B'
  const code = isB2B ? order?.merchantB2BZoneCode : null
  const from = isB2B ? String(order?.pickup_details?.from_pincode || '').trim() : ''
  const to = isB2B ? String(order?.delivery_details?.to_pincode || '').trim() : ''
  const merchantId = order?.merchantDetail?.key
  const [lane, setLane] = useState(null)

  useEffect(() => {
    setLane(null)
    if (!isB2B) return undefined
    let live = true
    loadGrid()
      .then(async ({ zones, lanes }) => {
        const saved = code ? lanes.get(code) : null
        if (saved?.from && saved?.to) return saved
        if (!from || !to) return null
        const ids = await zoneIdsOf([from, to], merchantId)
        const f = ids.get(from)
        const t = ids.get(to)
        if (!f || !t || f === NOT_SERVICEABLE || t === NOT_SERVICEABLE) return null
        return zones.get(f) && zones.get(t) ? { from: zones.get(f), to: zones.get(t), fromMap: true } : null
      })
      .then((l) => { if (live && l) setLane(l) })
      .catch(() => {})
    return () => { live = false }
  }, [isB2B, code, from, to, merchantId])

  return lane
}
